import React, { useMemo } from "react";
import { Spinner } from "../../../atoms/Spinner";
import { StatusBadge, BadgeTone } from "../../../atoms/StatusBadge";
import { CustomButton } from "../../../atoms/CustomButton";
import { SumPlanStatusEntryDTO } from "../../../../services/paymentModule";
import {
  formatPayModDate,
  parsePayModDate,
} from "../../../../services/planChange";
import { formatAmount } from "./types";
import {
  GuaranteeTargetPlan,
  SumPlanRow,
  sumPlanRows,
  toTargetPlan,
} from "./sumPlans";

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: "success",
  SUCCEEDED: "success",
  PROCESSING: "info",
  UNPAID: "warning",
  ERROR: "failure",
  CANCELED: "gray",
  ENDED: "gray",
  REFUNDED: "purple",
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: "Active",
  SUCCEEDED: "Active",
  PROCESSING: "Processing",
  UNPAID: "Unpaid",
  ERROR: "Error",
  CANCELED: "Cancelled",
  ENDED: "Ended",
  REFUNDED: "Refunded",
};

const money = (cents: number, currency: string) =>
  currency ? formatAmount(cents / 100, currency) : (cents / 100).toFixed(2);

const paymentLine = (row: SumPlanRow): string | null => {
  if (!row.lastPayment) return null;
  const amount = money(row.lastPayment.cents, row.lastPayment.currency);
  const date = parsePayModDate(row.lastPayment.date);
  return date.isValid()
    ? `Last payment ${amount} on ${date.format("ll")}`
    : `Last payment ${amount}`;
};

const periodLine = (row: SumPlanRow): string | null => {
  if (!row.currentPeriodEnd) return null;
  const date = formatPayModDate(row.currentPeriodEnd, "ll");
  if (row.cancelAtPeriodEnd) return `Cancels on ${date}`;
  if (row.active) return `Renews on ${date}`;
  return `Period ended ${date}`;
};

interface EntitySumPlansSectionProps {
  entries: SumPlanStatusEntryDTO[] | null;
  loading: boolean;
  onRefund: (plan: GuaranteeTargetPlan) => void;
  onCancel: (plan: GuaranteeTargetPlan) => void;
  onResume: (planCode: string) => void;
  busyPlanCode?: string;
}

const EntitySumPlansSection: React.FC<EntitySumPlansSectionProps> = ({
  entries,
  loading,
  onRefund,
  onCancel,
  onResume,
  busyPlanCode,
}) => {
  const rows = useMemo(() => sumPlanRows(entries ?? []), [entries]);

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Plans
      </p>

      {loading && entries === null ? (
        <div className="flex items-center gap-2 py-1.5 text-xs text-gray-400">
          <Spinner size="sm" />
          <span>Loading plans…</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">No SUM plan</p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.map((row) => {
            const busy = busyPlanCode === row.planCode;
            const payment = paymentLine(row);
            const period = periodLine(row);
            const canResume =
              row.hasSubscription && row.active && row.cancelAtPeriodEnd;
            const canCancel =
              row.hasSubscription && row.active && !row.cancelAtPeriodEnd;
            const hasActions = row.refundable || canResume || canCancel;
            return (
              <div
                key={row.planCode}
                className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3"
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {row.planDescription || row.planCode}
                    </p>
                    <p className="truncate font-mono text-xs text-gray-500">
                      {row.planCode}
                    </p>
                  </div>
                  <StatusBadge
                    color={STATUS_TONE[row.status] || "gray"}
                    size="xs"
                  >
                    {STATUS_LABEL[row.status] || row.status}
                  </StatusBadge>
                </div>

                {payment && <p className="text-xs text-gray-500">{payment}</p>}
                {period && <p className="text-xs text-gray-500">{period}</p>}

                {hasActions && (
                  <div className="flex flex-wrap justify-end gap-2">
                    {row.refundable && (
                      <CustomButton
                        onClick={() => onRefund(toTargetPlan(row))}
                        variant="outline"
                        size="sm"
                        title="Refund"
                        disabled={busy}
                      />
                    )}
                    {canResume && (
                      <CustomButton
                        onClick={() => onResume(row.planCode)}
                        variant="outline"
                        size="sm"
                        title="Resume"
                        isLoading={busy}
                        disabled={busy}
                      />
                    )}
                    {canCancel && (
                      <CustomButton
                        onClick={() => onCancel(toTargetPlan(row))}
                        variant="danger"
                        size="sm"
                        title="Cancel subscription"
                        isLoading={busy}
                        disabled={busy}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EntitySumPlansSection;
